import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Editor } from "@tiptap/react"

import { getCitationFilePath } from "@/api/api"
import {
  ArrowUpRight,
  Book,
  ChevronDown,
  ChevronUp,
  FileText,
  Globe,
  Mail,
} from "@/components/icons"
import { CitationIcon } from "@/features/chat/components/citation-icon"
import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { getCitationType } from "@/features/chat/components/citation.utils"
import { useDocumentTitles } from "@/hooks/use-documents"
import i18n from "@/languageConfig"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import {
  getCitationSourceForLookup,
  parseCitationParts,
  resolveCitationDisplayMeta,
} from "@/utils/citation-utils"
import { normalizeForMatching } from "@/utils/normalize-for-matching"

import { DocPreviewContent } from "./doc-preview-content"
import { useCanvasStore } from "../stores/canvas-store"

type CitationEntry = {
  citation: string
  index: number
}

type GroupedEntries = {
  web: CitationEntry[]
  m365: CitationEntry[]
  library: CitationEntry[]
  userDocuments: CitationEntry[]
  uploadedFiles: CitationEntry[]
}

function extractCitationsFromEditor(editor: Editor): CitationEntry[] {
  const seen = new Map<string, number>()
  let nextIndex = 0

  editor.state.doc.descendants((node) => {
    if (node.type.name === "canvasCitation") {
      const citation = node.attrs.citation as string
      if (citation && !seen.has(citation)) {
        seen.set(citation, nextIndex)
        nextIndex++
      }
    }
    return true
  })

  return Array.from(seen.entries()).map(([citation, index]) => ({ citation, index }))
}

function groupCitationEntries(entries: CitationEntry[]): GroupedEntries {
  const result: GroupedEntries = {
    web: [],
    m365: [],
    library: [],
    userDocuments: [],
    uploadedFiles: [],
  }

  for (const entry of entries) {
    const type = getCitationType(entry.citation)
    if (type === "web") {
      result.web.push(entry)
    } else if (type === "m365") {
      result.m365.push(entry)
    } else if (type === "library") {
      result.library.push(entry)
    } else {
      const source = getCitationSourceForLookup(entry.citation)
      if (source.startsWith("doc:")) {
        result.userDocuments.push(entry)
      } else {
        result.uploadedFiles.push(entry)
      }
    }
  }

  return result
}

type CanvasReferencesPanelProps = {
  editor: Editor
}

export function CanvasReferencesPanel({ editor }: CanvasReferencesPanelProps) {
  const { t } = useTranslation()
  const sessionId = useCanvasStore((state) => state.sessionId)
  const [isExpanded, setIsExpanded] = useState(false)
  const [citations, setCitations] = useState<CitationEntry[]>([])
  const hasAutoExpandedRef = useRef(false)

  useEffect(() => {
    const updateCitations = () => {
      const entries = extractCitationsFromEditor(editor)
      setCitations(entries)
      if (entries.length > 0 && !hasAutoExpandedRef.current) {
        hasAutoExpandedRef.current = true
        setIsExpanded(true)
      }
    }

    updateCitations()
    // Listen to "transaction" (not just "update") so citations are refreshed
    // when content is loaded with emitUpdate: false. Only run on doc-changing
    // transactions to avoid full traversal on selection/focus changes.
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }) => {
      if (transaction.docChanged) updateCitations()
    }
    editor.on("transaction", onTransaction)
    return () => {
      editor.off("transaction", onTransaction)
    }
  }, [editor])

  const openCitationPreview = useCallback(
    (citation: string) => {
      const parsed = parseCitationParts(citation)
      // Backend ignores the section segment for library citations; it only needs to be non-empty.
      // Fall back to the library ID so the URL never contains a double-slash.
      const libraryIdFallback = parsed.source.split(":")[0] || "library"
      const citationSection = sessionId ?? libraryIdFallback
      const documentUrl =
        getCitationFilePath(parsed.source, citationSection) +
        (parsed.page != null ? `#page=${parsed.page}` : "")
      const { title: citationTitle } = resolveCitationDisplayMeta(citation)
      const quoteSnippet = parsed.quote?.trim() ? normalizeForMatching(parsed.quote) : ""

      useContentSidebarStore.getState().pushPanel({
        id: "citation-preview",
        title:
          citationTitle || i18n.t("analysispanel.citation", { defaultValue: "Cited document" }),
        headerMode: "custom",
        renderHeader: (context) => (
          <CitationSidebarHeader
            closeable={context.closeable}
            downloadUrl={documentUrl}
            isFocusable={context.isFocusable}
            isFocused={context.isFocused}
            title={citationTitle}
            onBack={() => useContentSidebarStore.getState().popPanel()}
            onClose={context.onClose}
            onToggleFocus={context.onToggleFocus}
          />
        ),
        content: (
          <CitationViewerContent
            chunkSnippet=""
            documentUrl={documentUrl}
            quoteSnippet={quoteSnippet}
          />
        ),
        focusable: true,
        closeable: true,
        resizable: true,
        isDirty: () => false,
      })
    },
    [sessionId]
  )

  const openDocumentPreview = useCallback((documentId: string, title: string) => {
    useContentSidebarStore.getState().pushPanel({
      id: "citation-preview",
      title,
      headerMode: "custom",
      renderHeader: (context) => (
        <CitationSidebarHeader
          closeable={context.closeable}
          downloadUrl={`/documents/${documentId}`}
          isFocusable={context.isFocusable}
          isFocused={context.isFocused}
          title={title}
          onBack={() => useContentSidebarStore.getState().popPanel()}
          onClose={context.onClose}
          onToggleFocus={context.onToggleFocus}
        />
      ),
      content: <DocPreviewContent documentId={documentId} />,
      focusable: true,
      closeable: true,
      resizable: true,
      isDirty: () => false,
    })
  }, [])

  const docIds = citations
    .map((e) => {
      const source = getCitationSourceForLookup(e.citation)
      return source.startsWith("doc:") ? source.slice(4) : ""
    })
    .filter(Boolean)
  const documentTitles = useDocumentTitles(docIds)

  if (citations.length === 0) return null

  const grouped = groupCitationEntries(citations)

  return (
    <div className="border-t">
      <button
        className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-colors"
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span>{t("canvas.references", { defaultValue: "References" })}</span>
        <span className="flex items-center gap-1">
          <span className="bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 text-xs font-normal">
            {citations.length}
          </span>
          {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </span>
      </button>

      {isExpanded && (
        <div className="pb-2">
          {/* Web Sources */}
          {grouped.web.length > 0 && (
            <div>
              <div className="text-muted-foreground flex items-center gap-2 px-4 pt-2.5 pb-1 text-xs font-semibold tracking-wide uppercase">
                <Globe className="size-3.5" />
                <span>
                  {t("answer.web_sources", { defaultValue: "Web Sources" })} ({grouped.web.length})
                </span>
              </div>
              <div>
                {grouped.web.map((entry) => {
                  const meta = resolveCitationDisplayMeta(entry.citation)
                  const href = getCitationSourceForLookup(entry.citation)
                  return (
                    <a
                      key={entry.citation}
                      className="hover:bg-muted/50 flex w-full items-start gap-3 px-4 py-2.5 text-sm transition-colors"
                      href={href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span className="text-muted-foreground mt-0.5 shrink-0 font-mono text-xs">
                        [{entry.index + 1}]
                      </span>
                      <CitationIcon
                        citation={entry.citation}
                        className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                        type="web"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate leading-snug font-medium">
                          {meta.title}
                        </span>
                        {meta.subtitle && (
                          <span className="text-muted-foreground block truncate text-xs">
                            {meta.subtitle}
                          </span>
                        )}
                      </span>
                      <ArrowUpRight className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
                    </a>
                  )
                })}
              </div>
            </div>
          )}

          {/* M365 Sources */}
          {grouped.m365.length > 0 && (
            <div>
              <div className="text-muted-foreground flex items-center gap-2 px-4 pt-2.5 pb-1 text-xs font-semibold tracking-wide uppercase">
                <Mail className="size-3.5" />
                <span>
                  {t("answer.m365_sources", { defaultValue: "Microsoft 365" })} (
                  {grouped.m365.length})
                </span>
              </div>
              <div>
                {grouped.m365.map((entry) => {
                  const meta = resolveCitationDisplayMeta(entry.citation)
                  return (
                    <div
                      key={entry.citation}
                      className="flex w-full items-start gap-3 px-4 py-2.5 text-sm"
                    >
                      <span className="text-muted-foreground mt-0.5 shrink-0 font-mono text-xs">
                        [{entry.index + 1}]
                      </span>
                      <CitationIcon
                        citation={entry.citation}
                        className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                        type="m365"
                      />
                      <span className="min-w-0">
                        <span className="block truncate leading-snug font-medium">
                          {meta.title}
                        </span>
                        {meta.subtitle && (
                          <span className="text-muted-foreground block truncate text-xs">
                            {meta.subtitle}
                          </span>
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Library Documents */}
          {grouped.library.length > 0 && (
            <div>
              <div className="text-muted-foreground flex items-center gap-2 px-4 pt-2.5 pb-1 text-xs font-semibold tracking-wide uppercase">
                <Book className="size-3.5" />
                <span>
                  {t("answer.library_documents", { defaultValue: "Library Documents" })} (
                  {grouped.library.length})
                </span>
              </div>
              <div>
                {grouped.library.map((entry) => {
                  const meta = resolveCitationDisplayMeta(entry.citation)
                  return (
                    <button
                      key={entry.citation}
                      className="hover:bg-muted/50 flex w-full cursor-pointer items-start gap-3 px-4 py-2.5 text-left text-sm transition-colors"
                      type="button"
                      onClick={() => openCitationPreview(entry.citation)}
                    >
                      <span className="text-muted-foreground mt-0.5 shrink-0 font-mono text-xs">
                        [{entry.index + 1}]
                      </span>
                      <CitationIcon
                        citation={entry.citation}
                        className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                        type="library"
                      />
                      <span className="min-w-0">
                        <span className="block truncate leading-snug font-medium">
                          {meta.title}
                        </span>
                        {meta.subtitle && (
                          <span className="text-muted-foreground block truncate text-xs">
                            {meta.subtitle}
                          </span>
                        )}
                        {meta.page != null && (
                          <span className="text-muted-foreground block text-xs">
                            {t("answer.page", { defaultValue: "Page" })} {meta.page}
                          </span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* User Documents (doc:) */}
          {grouped.userDocuments.length > 0 && (
            <div>
              <div className="text-muted-foreground flex items-center gap-2 px-4 pt-2.5 pb-1 text-xs font-semibold tracking-wide uppercase">
                <FileText className="size-3.5" />
                <span>
                  {t("answer.user_documents", { defaultValue: "Documents" })} (
                  {grouped.userDocuments.length})
                </span>
              </div>
              <div>
                {grouped.userDocuments.map((entry) => {
                  const meta = resolveCitationDisplayMeta(entry.citation)
                  const source = getCitationSourceForLookup(entry.citation)
                  const documentId = source.startsWith("doc:") ? source.slice(4) : ""
                  const fallbackTitle = t("documents.untitled", { defaultValue: "Document" })
                  const displayTitle = documentId
                    ? (documentTitles[documentId] ?? fallbackTitle)
                    : meta.title
                  return (
                    <button
                      key={entry.citation}
                      className="hover:bg-muted/50 flex w-full cursor-pointer items-start gap-3 px-4 py-2.5 text-left text-sm transition-colors"
                      type="button"
                      onClick={() => {
                        if (documentId) openDocumentPreview(documentId, displayTitle)
                      }}
                    >
                      <span className="text-muted-foreground mt-0.5 shrink-0 font-mono text-xs">
                        [{entry.index + 1}]
                      </span>
                      <CitationIcon
                        citation={entry.citation}
                        className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                        type="file"
                      />
                      <span className="min-w-0">
                        <span className="block truncate leading-snug font-medium">
                          {displayTitle}
                        </span>
                        {meta.page != null && (
                          <span className="text-muted-foreground block text-xs">
                            {t("answer.page", { defaultValue: "Page" })} {meta.page}
                          </span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Uploaded Files (localdocs / bare files) */}
          {grouped.uploadedFiles.length > 0 && (
            <div>
              <div className="text-muted-foreground flex items-center gap-2 px-4 pt-2.5 pb-1 text-xs font-semibold tracking-wide uppercase">
                <FileText className="size-3.5" />
                <span>
                  {t("answer.uploaded_files", { defaultValue: "Uploaded Files" })} (
                  {grouped.uploadedFiles.length})
                </span>
              </div>
              <div>
                {grouped.uploadedFiles.map((entry) => {
                  const meta = resolveCitationDisplayMeta(entry.citation)
                  return (
                    <div
                      key={entry.citation}
                      className="flex w-full items-start gap-3 px-4 py-2.5 text-sm"
                    >
                      <span className="text-muted-foreground mt-0.5 shrink-0 font-mono text-xs">
                        [{entry.index + 1}]
                      </span>
                      <CitationIcon
                        citation={entry.citation}
                        className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
                        type="file"
                      />
                      <span className="min-w-0">
                        <span className="block truncate leading-snug font-medium">
                          {meta.title}
                        </span>
                        {meta.subtitle && (
                          <span className="text-muted-foreground block truncate text-xs">
                            {meta.subtitle}
                          </span>
                        )}
                        {meta.page != null && (
                          <span className="text-muted-foreground block text-xs">
                            {t("answer.page", { defaultValue: "Page" })} {meta.page}
                          </span>
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
